import * as React from 'react';
import { offeredPlans } from '@relay-towing/api';
import {
  BrandMark,
  Button,
  CALLS_PER_MONTH_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  Center,
  ContactInfoStep,
  EMPTY_ONBOARDING,
  Form,
  FormField,
  Input,
  MobileIntro,
  ONBOARDING_STEP_FIELDS,
  ONBOARDING_STEP_SCHEMAS,
  ONBOARDING_STEPS,
  OptionSheet,
  PlanPicker,
  StepIndicator,
  applyServerErrors,
  onboardingSchema,
  useZodForm,
  type OnboardingStep,
  type OnboardingValues,
} from '@relay-towing/ui';
import { Buildings, CreditCard, UserCircle } from '@phosphor-icons/react';
import { usePlanCatalogue } from '../../hooks/usePlan';
import { useSignIn } from '../../hooks/useSignIn';
import { useSignUp } from '../../hooks/useSignUp';
import { AddressInput } from '../shell/AddressInput';

const FORM_ID = 'sign-up';

const STEP_ICONS = { 1: UserCircle, 2: Buildings, 3: CreditCard } as const;

const TAKEN_EMAIL =
  'An account with this email already exists. Sign in instead.';

/**
 * Creating an account on a phone: the dashboard's /onboarding, the same
 * three steps (contact, company, plan) on the same schema, posted to the
 * same route, then signed in as the dashboard's wizard ends.
 *
 * There is no Figma frame for it. It is drawn as the Welcome frame is
 * (123:7108): the brand, a centred title over the steps, and the answer as
 * a big pill along the bottom with a quieter way back under it. A
 * designer's frame replaces this when there is one.
 *
 * Each step validates only its own slice, so Continue on the first never
 * complains about the plan. The email is checked as the first step is
 * left, where it can still be changed, rather than three steps later. A
 * refusal from the server goes under its field, and the wizard goes back
 * to the step that holds it.
 */
export function SignUpScreen() {
  const { signIn } = useSignIn();
  const { register, emailIsFree, turnstileRef } = useSignUp();
  const { data: catalogue } = usePlanCatalogue();
  const [step, setStep] = React.useState<OnboardingStep>(1);
  const [checking, setChecking] = React.useState(false);
  const form = useZodForm(onboardingSchema, {
    defaultValues: EMPTY_ONBOARDING,
  });
  const values = form.watch();
  const heading = ONBOARDING_STEPS.find((s) => s.id === step)?.heading;

  const set = (name: keyof OnboardingValues, value: string) =>
    form.setValue(name, value, { shouldValidate: form.formState.isSubmitted });

  const next = async () => {
    if (!(await form.trigger(ONBOARDING_STEP_FIELDS[step]))) return;
    if (step === 1) {
      setChecking(true);
      const free = await emailIsFree(form.getValues('email').trim());
      setChecking(false);
      if (!free) {
        form.setError('email', { type: 'server', message: TAKEN_EMAIL });
        return;
      }
    }
    setStep((step + 1) as OnboardingStep);
  };

  const create = (submitted: OnboardingValues) =>
    register.mutate(submitted, {
      onError: (error) => {
        applyServerErrors(form, error);
        const errors = form.formState.errors;
        const holding = ONBOARDING_STEPS.find(({ id }) =>
          ONBOARDING_STEP_FIELDS[id].some((name) => errors[name])
        );
        if (holding) setStep(holding.id);
      },
    });

  const isComplete = (id: OnboardingStep) =>
    ONBOARDING_STEP_SCHEMAS[id].safeParse(values).success;

  const steps = ONBOARDING_STEPS.map(({ id, title }, index) => ({
    id,
    title,
    icon: STEP_ICONS[id],
    complete: isComplete(id),
    // A step is reachable once every step before it is complete.
    reachable: ONBOARDING_STEPS.slice(0, index).every((s) => isComplete(s.id)),
  }));

  const last = step === 3;

  return (
    <MobileIntro
      align="top"
      brand={<BrandMark name="relay" size="md" />}
      title={heading}
      actions={
        <>
          {last ? (
            <Button
              type="submit"
              form={FORM_ID}
              size="xl"
              pill
              variant="soft"
              loading={register.isPending}
            >
              Create account
            </Button>
          ) : (
            <Button
              size="xl"
              pill
              variant="soft"
              loading={checking}
              onClick={() => void next()}
            >
              Continue
            </Button>
          )}
          {step === 1 ? (
            <Button variant="link" onClick={() => void signIn()}>
              I already have an account
            </Button>
          ) : (
            <Button
              variant="link"
              onClick={() => setStep((step - 1) as OnboardingStep)}
            >
              Back
            </Button>
          )}
        </>
      }
    >
      <StepIndicator
        orientation="horizontal"
        current={step}
        steps={steps}
        onSelect={(id) => setStep(id as OnboardingStep)}
      />
      <Form id={FORM_ID} form={form} gap={6} onSubmit={create}>
        {step === 1 ? <ContactInfoStep form={form} /> : null}
        {step === 2 ? (
          <>
            <FormField form={form} name="companyName" label="Company Name">
              {(field) => (
                <Input {...field} placeholder="Enter your company name" />
              )}
            </FormField>
            <FormField
              form={form}
              name="companyAddress"
              label="Company Address"
            >
              {(field) => (
                <AddressInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Street, city, state"
                  onPlace={(place) => {
                    set('companyStreet', place?.parts.streetAddress ?? '');
                    set('companyCity', place?.parts.city ?? '');
                    set('companyState', place?.parts.state ?? '');
                    set('companyZip', place?.parts.zipCode ?? '');
                  }}
                />
              )}
            </FormField>
            <FormField form={form} name="companySize" label="Company Size">
              {(field) => (
                <OptionSheet
                  title="Company size"
                  placeholder="Select company size"
                  options={COMPANY_SIZE_OPTIONS}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                />
              )}
            </FormField>
            <FormField
              form={form}
              name="averageCallsPerMonth"
              label="Average Calls Per Month"
            >
              {(field) => (
                <OptionSheet
                  title="Average calls per month"
                  placeholder="Select average calls"
                  options={CALLS_PER_MONTH_OPTIONS}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                />
              )}
            </FormField>
          </>
        ) : null}
        {last ? (
          <>
            <FormField form={form} name="selectedPlan">
              {(field) => (
                <PlanPicker
                  plans={catalogue ? offeredPlans(catalogue) : []}
                  selectedId={field.value}
                  onSelect={field.onChange}
                />
              )}
            </FormField>
            {/* Turnstile bot check, the small "verify you are human" box. */}
            <Center ref={turnstileRef} />
          </>
        ) : null}
      </Form>
    </MobileIntro>
  );
}
